import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { List, Users, UsersRound, User, Home, ChartLine, ChevronRight, Layers, Settings } from "lucide-react";
import { useEffect, useMemo, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { ancestorIds, visibleTreeRows } from "@/lib/pageTree";
import { useExpandedDepartments } from "@/hooks/useExpandedDepartments";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useUserRole } from "@/hooks/useUserRole";
import { useIsPlatformAdmin } from "@/hooks/useTenant";
import { useSubsheets } from "@/hooks/useSubsheets";
import { useSelectedSubsheet } from "@/hooks/useSelectedSubsheet";
import { ALL_ITEMS_ID } from "@/lib/constants";

// Deterministic tile colours for department entries, monday-style: each board
// in the list gets a small coloured square so the eye can find "the green one"
// faster than re-reading labels. Cycled by position — departments have no
// stored colour, and ordering is the admin-controlled order_index, so a
// department keeps its colour until the list itself is reordered.
const DEPT_TILE_COLORS = [
  "bg-sky-500",
  "bg-violet-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-teal-500",
];

/**
 * Left padding, in px, of a top-level department row (the old `pl-8`), and how
 * much each level of nesting adds. A parent's fold toggle (20px wide) sits just
 * before its tile, DEPT_TOGGLE_GAP clear of it.
 */
const DEPT_INDENT_BASE = 32;
const DEPT_INDENT_STEP = 16;
const DEPT_TOGGLE_WIDTH = 20;
const DEPT_TOGGLE_GAP = 4;

/** Cascade delay for the i-th entry of an expanding sub-list (top → bottom). */
const cascadeDelay = (i: number) => ({ animationDelay: `${50 + i * 70}ms` });

interface PrimaryNavProps {
  /**
   * Where this copy of the nav is drawn. Both variants are the same nav — same
   * entries, same order, same navy, same cascade and active rail — and differ
   * only in the size of a row: the sidebar is a pointer target sitting beside
   * the content all day, the drawer is a thumb target opened for one tap.
   */
  variant?: "sidebar" | "sheet";
  /** Sidebar only: icons-only rail. The drawer is never collapsed. */
  collapsed?: boolean;
  /**
   * Called when an entry that is a DESTINATION is taken — Home, All items, a
   * department, People, Teams, Settings. The drawer closes on those and stays
   * open on Backlog and Directory, which are not destinations so much as the
   * things whose sub-lists the reader came to open (see the cascade below).
   */
  onNavigate?: () => void;
  /** Trailing control for the footer block — the sidebar's collapse toggle. */
  footerSlot?: ReactNode;
}

/**
 * The application's primary navigation, in one place, drawn twice.
 *
 * The information architecture is the sidebar's: Home, Backlog and the
 * departments that scope it, Directory and its two tabs, and Settings pinned
 * to the bottom for admins. The mobile drawer is not a second, flatter menu
 * with its own idea of the app — it is this component in a Sheet, which is why
 * the two cannot drift apart.
 *
 * Personal items (Activity, Profile, Log out) are deliberately NOT here. They
 * live behind the avatar in the top bar at every width, so there is exactly one
 * place to look for them; the top bar makes room for the avatar on small
 * screens rather than the drawer growing a second home for it.
 */
const PrimaryNav = ({ variant = "sidebar", collapsed = false, onNavigate, footerSlot }: PrimaryNavProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { userId } = useUserProfile();
  const { data: userRole, isPending: roleLoading } = useUserRole();
  const { data: subsheets = [] } = useSubsheets();
  const { selectedSubsheetId, setSelectedSubsheetId } = useSelectedSubsheet();

  const isAuthenticated = !!userId;
  const isStaff = userRole === "superadmin" || userRole === "admin" || userRole === "member";
  const isAdmin = userRole === "admin" || userRole === "superadmin";
  // Platform admins reach Settings for its Tenants tab even where they hold
  // no admin role — see pages/Settings. Nothing else in the nav follows it.
  const isPlatformAdmin = useIsPlatformAdmin() === true;
  const showSettings = isAdmin || isPlatformAdmin;
  const sheet = variant === "sheet";

  // Sub-lists exist only while you are inside their section: Backlog's
  // departments cascade open when Backlog is hit and fold away elsewhere,
  // and Directory does the same with People/Teams. Unassigned is a value in
  // the Assigned header filter and Recently deleted a trailing section of the
  // list itself, so neither needs a nav entry anymore.
  const onBacklog = location.pathname.startsWith("/backlog");
  const onDirectory =
    location.pathname.startsWith("/directory") ||
    location.pathname.startsWith("/people") ||
    location.pathname.startsWith("/teams");
  const directoryTab =
    (location.pathname.startsWith("/directory") && new URLSearchParams(location.search).get("tab")) ||
    (location.pathname.startsWith("/teams") ? "teams" : "people");

  // Selecting a department scopes the backlog, so the click also lands there —
  // the departments ARE the backlog's sub-navigation in this layout.
  const selectDepartment = (id: string) => {
    setSelectedSubsheetId(id);
    if (location.pathname !== "/backlog") navigate("/backlog");
    onNavigate?.();
  };

  // Navy sidebar, XBG design system: white-alpha washes for hover/active and
  // an inset cyan rail marking the active entry. The drawer takes the same
  // treatment one size up — a 36px row is a comfortable pointer target and a
  // cramped thumb one, so the drawer's rows clear 44px.
  //
  // The inactive step is the sidebar text token at 85%, not 75%. An inactive
  // entry is the state nearly every row in this nav is in, so it is the one
  // that decides whether the nav is readable at all; at 75% of the old 88%
  // token it measured 5.0:1 on the light navy — over the AA line on paper and
  // visibly grey in the app. With the token lifted to 95% (index.css) this
  // lands at 6.9:1 light / 7.6:1 dark, and still sits a clear step below the
  // pure white the active and hovered rows take, which is what keeps "where I
  // am" legible as a difference rather than as a brightness contest.
  const linkClass = (active: boolean) =>
    cn(
      "group press-pop flex w-full items-center gap-2.5 rounded-md px-2.5 font-semibold transition-colors",
      sheet ? "h-11 text-sm" : "h-9 text-[13.5px]",
      active
        ? "bg-white/[0.18] text-white shadow-[inset_3px_0_0_hsl(var(--sidebar-primary))]"
        : "text-sidebar-foreground/85 hover:bg-white/10 hover:text-white",
      collapsed && "justify-center px-0",
    );

  const subLinkClass = (active: boolean) =>
    cn(
      linkClass(active),
      "cascade-item font-medium",
      sheet ? "h-10 text-[13.5px]" : "h-8 text-[13px]",
      !collapsed && "pl-8",
    );

  const showDepartments = isAuthenticated && !roleLoading && isStaff && subsheets.length > 0;

  // Colour follows the admin-controlled order_index position, not the row's
  // place on screen, so folding a branch does not repaint the rows below it.
  const colorIndex = useMemo(() => new Map(subsheets.map((s, i) => [s.id, i])), [subsheets]);
  const { expanded: expandedDepartments, toggle: toggleDepartment, reveal } = useExpandedDepartments();
  // Whatever department is selected stays reachable: its ancestors unfold.
  useEffect(() => {
    if (selectedSubsheetId) reveal(ancestorIds(subsheets, selectedSubsheetId));
  }, [selectedSubsheetId, subsheets, reveal]);
  // The icon rail has no fold toggles, so it ignores folds: every department
  // stays reachable there.
  const departmentRows = useMemo(
    () => visibleTreeRows(subsheets, collapsed ? new Set(subsheets.map((s) => s.id)) : expandedDepartments),
    [subsheets, expandedDepartments, collapsed],
  );

  return (
    <>
      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-2.5 pb-3 pt-4">
        <NavLink to="/" onClick={onNavigate} className={linkClass(location.pathname === "/")} title="Home">
          <Home className="h-4 w-4 shrink-0" />
          {!collapsed && <span className="truncate">Home</span>}
        </NavLink>

        {/* Insights sits directly under Home because it is the same reader's
            next question — Home says what is on your plate this week, Insights
            says what is happening to the whole backlog. It used to be reachable
            only from a text link in Home's footer line, which made a whole page
            a footnote of another one; the nav is the entry point now. Admin-only,
            exactly as that link was: the page's breakdowns are an admin view. */}
        {isAdmin && (
          <NavLink
            to="/insights"
            onClick={onNavigate}
            className={linkClass(location.pathname.startsWith("/insights"))}
            title="Insights"
          >
            <ChartLine className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="truncate">Insights</span>}
          </NavLink>
        )}

        <div className="mx-1 my-2 border-t border-white/10" />

        {/* Backlog and Directory are not leaves: taking one opens the sub-list
            under it, which in the drawer is the reason the reader opened the
            drawer. So they navigate WITHOUT closing it, and the entry the
            cascade reveals is the one that closes it. */}
        <NavLink to="/backlog" className={linkClass(onBacklog)} title="Backlog">
          <List className="h-4 w-4 shrink-0" />
          {!collapsed && <span className="truncate">Backlog</span>}
          {!collapsed && (
            <ChevronRight
              className={cn("ml-auto h-3.5 w-3.5 shrink-0 transition-transform duration-200", onBacklog && "rotate-90")}
            />
          )}
        </NavLink>

        {showDepartments && onBacklog && (
          <>
            {/* All items leads: it is the default scope, and the departments
                narrow from there. keys retrigger the cascade per visit. */}
            <button
              key={`all-${location.pathname}`}
              type="button"
              onClick={() => selectDepartment(ALL_ITEMS_ID)}
              title="All items"
              style={cascadeDelay(0)}
              className={subLinkClass(onBacklog && selectedSubsheetId === ALL_ITEMS_ID)}
            >
              <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded bg-white/25 text-white">
                <Layers className="h-3 w-3" />
              </span>
              {!collapsed && <span className="truncate text-left">All items</span>}
            </button>
            {/* Departments nest (#9304): each sits under its parent, and a
                parent with children carries its own fold toggle beside the
                row, so opening a branch never also navigates. The icon rail
                has no room for indentation or toggles, so it draws every
                department flat. */}
            {departmentRows.map(({ page: subsheet, depth, hasChildren, expanded }, i) => (
              <div key={subsheet.id} className="relative">
                <button
                  type="button"
                  onClick={() => selectDepartment(subsheet.id)}
                  title={subsheet.name}
                  style={{
                    ...cascadeDelay(i + 1),
                    ...(collapsed ? {} : { paddingLeft: DEPT_INDENT_BASE + depth * DEPT_INDENT_STEP }),
                  }}
                  className={subLinkClass(onBacklog && selectedSubsheetId === subsheet.id)}
                >
                  <span
                    className={cn(
                      "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded text-[9px] font-bold uppercase text-white",
                      DEPT_TILE_COLORS[(colorIndex.get(subsheet.id) ?? 0) % DEPT_TILE_COLORS.length],
                    )}
                  >
                    {subsheet.name.charAt(0)}
                  </span>
                  {!collapsed && <span className="truncate text-left">{subsheet.name}</span>}
                </button>
                {!collapsed && hasChildren && (
                  <button
                    type="button"
                    onClick={() => toggleDepartment(subsheet.id)}
                    aria-expanded={expanded}
                    aria-label={`${expanded ? "Collapse" : "Expand"} ${subsheet.name}`}
                    style={{
                      ...cascadeDelay(i + 1),
                      left: DEPT_INDENT_BASE - DEPT_TOGGLE_WIDTH - DEPT_TOGGLE_GAP + depth * DEPT_INDENT_STEP,
                    }}
                    // Centred with inset-y-0 + my-auto, not a translate: the
                    // cascade animation's `both` fill pins transform to none,
                    // which would undo -translate-y-1/2 and drop the arrow.
                    className="cascade-item absolute inset-y-0 my-auto flex h-5 w-5 items-center justify-center rounded text-sidebar-foreground/70 hover:bg-white/10 hover:text-white"
                  >
                    <ChevronRight
                      className={cn("h-3.5 w-3.5 transition-transform duration-200", expanded && "rotate-90")}
                    />
                  </button>
                )}
              </div>
            ))}
          </>
        )}

        {isStaff && (
          <>
            <div className="mx-1 my-2 border-t border-white/10" />
            <NavLink to="/directory" className={linkClass(onDirectory)} title="Directory">
              <Users className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">Directory</span>}
              {!collapsed && (
                <ChevronRight
                  className={cn(
                    "ml-auto h-3.5 w-3.5 shrink-0 transition-transform duration-200",
                    onDirectory && "rotate-90",
                  )}
                />
              )}
            </NavLink>
            {onDirectory && (
              <>
                <button
                  key={`people-${location.pathname}`}
                  type="button"
                  onClick={() => {
                    navigate("/directory");
                    onNavigate?.();
                  }}
                  title="People"
                  style={cascadeDelay(0)}
                  className={subLinkClass(directoryTab !== "teams")}
                >
                  <User className="h-3.5 w-3.5 shrink-0" />
                  {!collapsed && <span className="truncate text-left">People</span>}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    navigate("/directory?tab=teams");
                    onNavigate?.();
                  }}
                  title="Teams"
                  style={cascadeDelay(1)}
                  className={subLinkClass(directoryTab === "teams")}
                >
                  <UsersRound className="h-3.5 w-3.5 shrink-0" />
                  {!collapsed && <span className="truncate text-left">Teams</span>}
                </button>
              </>
            )}
          </>
        )}
      </nav>

      {/* The floor of the nav: the collapse control first (chrome for the
          rail, not a destination), then the rule, then Settings pinned last —
          the divider separates the rail chrome from the final destination
          rather than from the list above. */}
      <div className="flex flex-col gap-0.5 px-2.5 pb-3">
        {footerSlot}
        {/* In the drawer (no collapse slot) this same rule still separates
            Settings from the nav list above it. */}
        {showSettings && <div className="mx-1 mb-1 border-t border-white/10" />}
        {showSettings && (
          /* Configuration only, and admin-only as a result: the avatar menu
             stays personal, backlog views live under Backlog, and the five
             config pages are tabs inside this one destination. Named for who
             it is for rather than for what it holds — a member who never sees
             it should not have to learn that "Settings" was never theirs, and
             an admin arriving at it knows it is the workspace's settings and
             not their own. The route guards itself as well (pages/Settings). */
          <NavLink
            to="/settings"
            onClick={onNavigate}
            title="Admin settings"
            className={linkClass(location.pathname.startsWith("/settings"))}
          >
            <Settings className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="truncate">Admin settings</span>}
          </NavLink>
        )}
      </div>
    </>
  );
};

export default PrimaryNav;
